import { Image as FabricImage } from 'fabric';
import { useCallback } from 'react';

import { CANVAS_DIMENSIONS } from '../constants';
import { getDataUrlSizeInBytes } from '../utils/getDataUrlSizeInBytes';

// ─── Helpers ──────────────────────────────────────────────────────────────────

interface ObjWithGroupAndData {
  data?: { label?: string; id?: string; href?: string; [key: string]: unknown };
  group?: ObjWithGroupAndData;
}

/**
 * Retorna `true` se o objeto for o grupo Banner CTA ou qualquer descendente dele.
 * Usado para proteger o Banner CTA de exclusão, duplicação e movimento.
 */
export function isBannerCtaOrChild(obj: FabricObject): boolean {
  const o = obj as unknown as ObjWithGroupAndData;
  if (o.data?.label === 'Banner CTA') return true;
  if (o.group?.data?.label === 'Banner CTA') return true;
  if (o.group?.group?.data?.label === 'Banner CTA') return true;
  return false;
}

/**
 * Retorna o grupo Banner CTA a partir do próprio grupo ou de qualquer descendente.
 * Necessário porque com `interactive: true` o objeto ativo pode ser um filho.
 */
export function getBannerCtaGroup(
  obj: FabricObject,
): (ObjWithGroupAndData & { getObjects: () => FabricObject[] }) | null {
  const o = obj as unknown as ObjWithGroupAndData & { getObjects: () => FabricObject[] };
  if (o.data?.label === 'Banner CTA') return o;
  const p1 = o.group as unknown as
    | (ObjWithGroupAndData & { getObjects: () => FabricObject[] })
    | undefined;
  if (p1?.data?.label === 'Banner CTA') return p1;
  const p2 = p1?.group as unknown as
    | (ObjWithGroupAndData & { getObjects: () => FabricObject[] })
    | undefined;
  if (p2?.data?.label === 'Banner CTA') return p2;
  return null;
}

import type { ActiveSelection, Canvas, Object as FabricObject } from 'fabric';
import type { SetStateAction } from 'jotai';
import type { SelectedObjectState } from '../store/atoms/editorSelectionAtom';
import type { ActiveDevice } from '../store/atoms/editorUiAtom';
import type { SlideData, VariantKey } from '../store/atoms/slideAtom';

export interface FabricTextProps {
  text: string;
  fontSize: number;
  fontFamily: string;
  fontWeight: string;
  fontStyle: string;
  lineHeight: number;
  charSpacing: number;
}

export interface CanvasObjectMeta {
  objectId: string;
  type: string;
  label: string;
}

export interface UseObjectTransformParams {
  readonly getActiveCanvas: () => Canvas | null;
  readonly syncSelectionAtom: (canvas: Canvas) => void;
  readonly saveCurrentSlide: (key: VariantKey) => void;
  readonly variantKey: VariantKey;
  readonly setSlide: (update: SetStateAction<SlideData>) => void;
  readonly setSelection: (value: SetStateAction<SelectedObjectState | null>) => void;
  readonly activeDevice: ActiveDevice;
  readonly alert: (options: {
    alertType: 'success' | 'warning' | 'danger' | 'information';
    title: string;
  }) => void;
}

/**
 * Operações de transformação, estilo e seleção sobre o objeto ativo no canvas.
 * Inclui também `getPreviewDataUrl` e `setBannerCtaHref`.
 *
 * @param params - Canvas ativo, funções de sincronização e contexto de variant/device
 * @returns Todas as operações de manipulação de objetos expostas pelo editor
 */
export function useObjectTransform({
  getActiveCanvas,
  syncSelectionAtom,
  saveCurrentSlide,
  variantKey,
  setSlide,
  setSelection,
  activeDevice,
  alert,
}: UseObjectTransformParams) {
  const deleteSelected = useCallback(() => {
    const canvas = getActiveCanvas();
    if (!canvas) return;
    const obj = canvas.getActiveObject();
    if (!obj) return;
    if (obj.type === 'activeSelection') {
      // Exclui apenas objetos que não são Banner CTA nem filhos dele
      (obj as ActiveSelection)
        .getObjects()
        .filter(o => !isBannerCtaOrChild(o))
        .forEach(o => {
          canvas.remove(o);
        });
    } else {
      if (isBannerCtaOrChild(obj)) return;
      canvas.remove(obj);
    }
    canvas.discardActiveObject();
    canvas.requestRenderAll();
    setSelection(null);
    saveCurrentSlide(variantKey);
  }, [getActiveCanvas, setSelection, saveCurrentSlide, variantKey]);

  const duplicateSelected = useCallback(
    (pushSnapshot: (key: string) => void) => {
      const canvas = getActiveCanvas();
      if (!canvas) return;
      const obj = canvas.getActiveObject();
      if (!obj || isBannerCtaOrChild(obj)) return;
      void obj.clone(['data']).then((cloned: FabricObject) => {
        cloned.set({
          left: (obj.left ?? 0) + 20,
          top: (obj.top ?? 0) + 20,
          data: {
            id: crypto.randomUUID(),
            label: `${(obj as { data?: { label?: string } }).data?.label ?? 'Obj'} cópia`,
          },
        });
        canvas.add(cloned);
        canvas.setActiveObject(cloned);
        canvas.requestRenderAll();
        saveCurrentSlide(variantKey);
        pushSnapshot(variantKey);
      });
    },
    [getActiveCanvas, saveCurrentSlide, variantKey],
  );

  const bringForward = useCallback(() => {
    const canvas = getActiveCanvas();
    const obj = canvas?.getActiveObject();
    if (canvas && obj) {
      canvas.bringObjectForward(obj);
      canvas.requestRenderAll();
      saveCurrentSlide(variantKey);
    }
  }, [getActiveCanvas, saveCurrentSlide, variantKey]);

  const sendBackward = useCallback(() => {
    const canvas = getActiveCanvas();
    const obj = canvas?.getActiveObject();
    if (canvas && obj) {
      canvas.sendObjectBackwards(obj);
      canvas.requestRenderAll();
      saveCurrentSlide(variantKey);
    }
  }, [getActiveCanvas, saveCurrentSlide, variantKey]);

  const setObjectPosition = useCallback(
    (x: number, y: number) => {
      const canvas = getActiveCanvas();
      const obj = canvas?.getActiveObject();
      if (canvas && obj) {
        obj.set({ left: x, top: y });
        canvas.requestRenderAll();
        syncSelectionAtom(canvas);
        saveCurrentSlide(variantKey);
      }
    },
    [getActiveCanvas, syncSelectionAtom, saveCurrentSlide, variantKey],
  );

  const setObjectSize = useCallback(
    (width: number, height: number) => {
      const canvas = getActiveCanvas();
      const obj = canvas?.getActiveObject();
      if (canvas && obj) {
        obj.set({
          scaleX: width / (obj.width ?? 1),
          scaleY: height / (obj.height ?? 1),
        });
        canvas.requestRenderAll();
        syncSelectionAtom(canvas);
        saveCurrentSlide(variantKey);
      }
    },
    [getActiveCanvas, syncSelectionAtom, saveCurrentSlide, variantKey],
  );

  const setObjectAngle = useCallback(
    (angle: number) => {
      const canvas = getActiveCanvas();
      const obj = canvas?.getActiveObject();
      if (!canvas || !obj) return;
      if (isBannerCtaOrChild(obj)) return;
      obj.set({ angle });
      canvas.requestRenderAll();
      syncSelectionAtom(canvas);
      saveCurrentSlide(variantKey);
    },
    [getActiveCanvas, syncSelectionAtom, saveCurrentSlide, variantKey],
  );

  const setObjectOpacity = useCallback(
    (opacity: number) => {
      const canvas = getActiveCanvas();
      const obj = canvas?.getActiveObject();
      if (canvas && obj) {
        obj.set({ opacity: opacity / 100 });
        canvas.requestRenderAll();
        syncSelectionAtom(canvas);
        saveCurrentSlide(variantKey);
      }
    },
    [getActiveCanvas, syncSelectionAtom, saveCurrentSlide, variantKey],
  );

  const setFill = useCallback(
    (color: string) => {
      const canvas = getActiveCanvas();
      const obj = canvas?.getActiveObject();
      if (canvas && obj) {
        obj.set({ fill: color });
        canvas.requestRenderAll();
        syncSelectionAtom(canvas);
        saveCurrentSlide(variantKey);
      }
    },
    [getActiveCanvas, syncSelectionAtom, saveCurrentSlide, variantKey],
  );

  const setStroke = useCallback(
    (color: string, width: number) => {
      const canvas = getActiveCanvas();
      const obj = canvas?.getActiveObject();
      if (canvas && obj) {
        obj.set({ stroke: color, strokeWidth: width });
        canvas.requestRenderAll();
        syncSelectionAtom(canvas);
        saveCurrentSlide(variantKey);
      }
    },
    [getActiveCanvas, syncSelectionAtom, saveCurrentSlide, variantKey],
  );

  const setTextProps = useCallback(
    (props: Partial<FabricTextProps>) => {
      const canvas = getActiveCanvas();
      const obj = canvas?.getActiveObject();
      if (!canvas || !obj) return;
      if (['textbox', 'i-text', 'text'].includes(obj.type ?? '')) {
        obj.set(props as Partial<FabricObject>);
        canvas.requestRenderAll();
        syncSelectionAtom(canvas);
        saveCurrentSlide(variantKey);
      }
    },
    [getActiveCanvas, syncSelectionAtom, saveCurrentSlide, variantKey],
  );

  const setBackground = useCallback(
    (color: string) => {
      const canvas = getActiveCanvas();
      if (!canvas) return;
      canvas.backgroundColor = color;
      canvas.requestRenderAll();
      saveCurrentSlide(variantKey);
      setSlide(prev => ({
        ...prev,
        variants: {
          ...prev.variants,
          [variantKey]: { ...prev.variants[variantKey], backgroundColor: color },
        },
      }));
    },
    [getActiveCanvas, saveCurrentSlide, variantKey, setSlide],
  );

  const setBackgroundImage = useCallback(
    (dataUrl: string) => {
      const sizeInBytes = getDataUrlSizeInBytes(dataUrl);
      if (sizeInBytes > 5 * 1024 * 1024) {
        alert({ alertType: 'warning', title: 'A imagem de fundo deve ter no máximo 5MB' });
        return;
      }
      const canvas = getActiveCanvas();
      if (!canvas) return;
      void FabricImage.fromURL(dataUrl).then(img => {
        const canvasWidth = canvas.getWidth() / canvas.getZoom();
        const canvasHeight = canvas.getHeight() / canvas.getZoom();

        const imgWidth = img.width;
        const imgHeight = img.height;

        // Calcula a escala para preencher TODO o canvas (cover)
        const scaleX = canvasWidth / imgWidth;
        const scaleY = canvasHeight / imgHeight;
        const scale = Math.max(scaleX, scaleY); // Usa o MAIOR fator para preencher tudo

        // Calcula a posição para centralizar (já que a imagem pode ultrapassar)
        const scaledWidth = imgWidth * scale;
        const scaledHeight = imgHeight * scale;
        const left = (canvasWidth - scaledWidth) / 2;
        const top = (canvasHeight - scaledHeight) / 2;

        img.set({
          scaleX: scale,
          scaleY: scale,
          originX: 'left' as const,
          originY: 'top' as const,
          left: left,
          top: top,
        });

        canvas.backgroundImage = img;
        canvas.requestRenderAll();
        saveCurrentSlide(variantKey);
        setSlide(prev => ({
          ...prev,
          variants: {
            ...prev.variants,
            [variantKey]: { ...prev.variants[variantKey], backgroundImage: dataUrl },
          },
        }));
      });
    },
    [getActiveCanvas, saveCurrentSlide, variantKey, setSlide, alert],
  );

  const removeBackgroundImage = useCallback(() => {
    const canvas = getActiveCanvas();
    if (!canvas) return;
    Object.assign(canvas, { backgroundImage: undefined });
    canvas.requestRenderAll();
    saveCurrentSlide(variantKey);
    setSlide(prev => {
      const { backgroundImage: _, ...restVariant } = prev.variants[variantKey];
      return { ...prev, variants: { ...prev.variants, [variantKey]: restVariant } };
    });
  }, [getActiveCanvas, saveCurrentSlide, variantKey, setSlide]);

  const selectObject = useCallback(
    (objectId: string) => {
      const canvas = getActiveCanvas();
      if (!canvas) return;
      const obj = canvas
        .getObjects()
        .find(o => (o as { data?: { id?: string } }).data?.id === objectId);
      if (obj) {
        canvas.setActiveObject(obj);
        canvas.requestRenderAll();
        syncSelectionAtom(canvas);
      }
    },
    [getActiveCanvas, syncSelectionAtom],
  );

  const getObjects = useCallback((): CanvasObjectMeta[] => {
    const canvas = getActiveCanvas();
    if (!canvas) return [];
    return canvas.getObjects().map(o => ({
      objectId: (o as { data?: { id?: string } }).data?.id ?? '',
      type: o.type ?? 'unknown',
      label: (o as { data?: { label?: string } }).data?.label ?? o.type ?? 'Objeto',
    }));
  }, [getActiveCanvas]);

  const setBannerCtaHref = useCallback(
    (href: string) => {
      const canvas = getActiveCanvas();
      if (!canvas) return;
      const obj = canvas.getActiveObject();
      if (!obj) return;
      interface GroupLike {
        getObjects: () => FabricObject[];
        data?: { label?: string; href?: string; [key: string]: unknown };
      }
      // Funciona tanto quando o Banner CTA está selecionado diretamente quanto
      // quando um filho está ativo (interactive: true faz o filho ser o objeto ativo).
      const bannerGroup = getBannerCtaGroup(obj);
      if (!bannerGroup) return;
      const btnGroup = (bannerGroup as unknown as GroupLike)
        .getObjects()
        .find(o => o.type === 'group' && (o as unknown as GroupLike).data?.label === 'Saiba mais');
      if (!btnGroup) return;
      (btnGroup as unknown as GroupLike).data = {
        ...(btnGroup as unknown as GroupLike).data,
        href,
      };
      saveCurrentSlide(variantKey);
      setSelection(prev => (prev ? { ...prev, href } : prev));
    },
    [getActiveCanvas, saveCurrentSlide, variantKey, setSelection],
  );

  const getPreviewDataUrl = useCallback((): string => {
    const canvas = getActiveCanvas();
    if (!canvas) return '';
    const el = canvas.getElement();
    const W =
      activeDevice === 'desktop' ? CANVAS_DIMENSIONS.desktop.width : CANVAS_DIMENSIONS.mobile.width;
    const H =
      activeDevice === 'desktop'
        ? CANVAS_DIMENSIONS.desktop.height
        : CANVAS_DIMENSIONS.mobile.height;
    const offscreen = document.createElement('canvas');
    offscreen.width = W;
    offscreen.height = H;
    const ctx2d = offscreen.getContext('2d');
    if (!ctx2d) return '';
    ctx2d.drawImage(el, 0, 0, W, H);
    return offscreen.toDataURL('image/png');
  }, [getActiveCanvas, activeDevice]);

  return {
    deleteSelected,
    duplicateSelected,
    bringForward,
    sendBackward,
    setObjectPosition,
    setObjectSize,
    setObjectAngle,
    setObjectOpacity,
    setFill,
    setStroke,
    setTextProps,
    setBackground,
    setBackgroundImage,
    removeBackgroundImage,
    selectObject,
    getObjects,
    setBannerCtaHref,
    getPreviewDataUrl,
  } as const;
}
